import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import FolderTabs from "../../../components/common/FolderTabs";
import { useToast } from "../../../contexts/ToastContext";
import { setCurrentProject } from "../../../redux/project/projectSlice";
import { projectsService } from "../../../services/projects";

const DEFAULT_SPEC_FILES = [
  { filename: "SITE.md", empty_comment: "<!-- Add site identity, audience, and principles here. -->" },
  { filename: "DATA.md", empty_comment: "<!-- Add dataset roles, entities, and data pipeline notes here. -->" },
  { filename: "CONTENT.md", empty_comment: "<!-- Add voice, sourcing rules, and page composition notes here. -->" },
  { filename: "DESIGN.md", empty_comment: "<!-- Add visual direction and design cues here. -->" },
];

const loadSiteSpecFiles = () => {
  try {
    const config = JSON.parse(localStorage.getItem("currentUser") || "{}");
    const files = config.env?.site_spec_files;
    if (Array.isArray(files) && files.length) {
      return files;
    }
  } catch (_) {}
  return DEFAULT_SPEC_FILES;
};

const Specs = () => {
  const dispatch = useDispatch();
  const { success, error: toastError } = useToast();
  const project = useSelector((state) => state.project.currentProject);
  const registry = useMemo(() => loadSiteSpecFiles(), []);
  const specs = (project?.site_plan && project.site_plan.specs) || {};
  const [activeSpec, setActiveSpec] = useState(registry[0]?.filename || "SITE.md");
  const [description, setDescription] = useState(project?.description || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!registry.some((item) => item.filename === activeSpec) && registry[0]) {
      setActiveSpec(registry[0].filename);
    }
  }, [activeSpec, registry]);

  useEffect(() => {
    setDescription(project?.description || "");
  }, [project?.hash, project?.description]);

  const tabs = registry.map((item) => ({ id: item.filename, label: item.filename }));
  const meta = registry.find((item) => item.filename === activeSpec) || registry[0];
  const content = typeof specs[activeSpec] === "string" ? specs[activeSpec] : "";
  const savedDescription = project?.description || "";
  const canSave = description !== savedDescription && !saving;

  const saveDescription = () => {
    if (!project?.hash || !canSave) return;
    setSaving(true);
    projectsService.updateProject(
      project.hash,
      { description: description.trim() },
      (response) => {
        dispatch(setCurrentProject(response.data));
        success("Saved", 2000);
        setSaving(false);
      },
      () => {
        toastError("Could not save description");
        setSaving(false);
      },
    );
  };

  return (
    <div className="max-w-4xl space-y-8">
      <div className="space-y-4">
        <div>
          <h2 className="text-sm font-semibold">Site specs</h2>
          <p className="mt-1 text-xs opacity-60">
            Read-only for now. These live in <code className="text-[11px]">/specs/</code> in the site repo.
          </p>
        </div>
        <FolderTabs tabs={tabs} activeId={activeSpec} onChange={setActiveSpec}>
          <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded-lg border border-theme-secondary bg-theme-main p-4 font-mono text-xs leading-5">
            {content.trim() || meta?.empty_comment || "<!-- Add details here. -->"}
          </pre>
        </FolderTabs>
      </div>

      <section>
        <h2 className="mb-1 text-sm font-semibold">Meta Description</h2>
        <p className="mb-4 text-xs opacity-60">
          Short public summary of the site. Used for description and tagline in site data.
        </p>
        <label className="block text-xs font-medium">
          <textarea
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            rows={4}
            className="mt-2 w-full rounded-lg border border-theme-secondary bg-theme-main px-4 py-3 text-sm leading-5"
            placeholder="A directory of dog-friendly hotels and stays, with pet policies, fees, and neighborhood guides."
          />
        </label>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            disabled={!canSave}
            onClick={saveDescription}
            className="rounded-lg bg-theme-primary px-5 py-3 text-sm font-semibold text-theme-button disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </section>
    </div>
  );
};

export default Specs;
